import React from 'react';
import JanelaModal from '../../JanelaModal';
import MotivosDoServico, { motivosDoServico } from '../shared/MotivosDoServico';
import ObservacaoDeCobranca from '../shared/ObservacaoDeCobranca';
import { MARK, BILLING_MODELS } from '../../../constants';
import { nomeDoCliente } from '../../../data/clients';
import {
  calcularEntrada, entradaAEmitir, MOTIVO_DA_ENTRADA,
  problemasDaEntrada, PROBLEMA_DA_ENTRADA,
} from '../../../utils/calculations/entrada';

/**
 * O dinheiro de UM serviço, com as ações que ele comporta.
 *
 * É o irmão do modal de etapas: aquele responde "o que foi feito", este
 * responde "quanto disso virou dinheiro e onde ele está". Os dois abrem da
 * mesma linha, no Dashboard, e é isso que permite as abas de Serviços e de
 * Financeiro saírem — elas eram a terceira e a quarta lista dos mesmos
 * serviços, separadas apenas pelos botões que cada uma carregava.
 *
 * CUSTO ENTRA AQUI, e não numa quarta ação. Custo é dinheiro, e quem lança
 * custo é quem fatura.
 *
 * O que este modal NÃO faz: cobrança em lote e cobrança por cliente. Faturar
 * sete serviços no fim do mês não é abrir sete modais, e quem deve é o
 * cliente, não o serviço. Essas duas coisas vivem no relatório financeiro.
 */

const reais = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

/** Uma cifra do resumo. Só aparece quando tem o que dizer. */
const Numero = ({ rotulo, valor, apoio, forte = false }) => (
  <div>
    <p className="text-xs text-slate-500">{rotulo}</p>
    <p className={`tabular-nums ${forte
      ? 'text-lg font-semibold text-slate-900'
      : 'text-base font-medium text-slate-800'}`}>
      R$ {reais(valor)}
    </p>
    {apoio && <p className="text-xs text-slate-400">{apoio}</p>}
  </div>
);

const FinanceiroDoServicoModal = ({
  show,
  service,
  onClose,
  currentLab,
  clients = [],
  services = [],
  loading,
  canEdit,
  canManageServices,
  calculateServiceValue,
  conta = {},
  parcelas = [],
  entradaPaga = 0,
  entradaEmitida = null,
  // `null` significa "a leitura das faturas falhou". Diferente de `{}`, que é
  // "nenhuma emitida": sem saber o que existe, não se oferece cobrar.
  leituraFalhou = false,
  forma = null,
  openCostsModal,
  handleBillService,
  handleChargeEntrada,
  handleMarkAsPaid,
  handleTogglePago,
  handleRefaturar,
  handleRefaturarEntrada,
  generateBillingPDF,
  salvarObservacaoDeCobranca,
}) => {
  if (!show || !service) return null;

  const valorCalculado = calculateServiceValue(service);
  const semCobranca = service.billing_model === BILLING_MODELS.SEM_COBRANCA;
  const hoje = new Date().toISOString().slice(0, 10);

  // As parcelas do documento da entrada, que tem vida própria: número,
  // vencimento e PDF separados dos do serviço.
  const parcelasDaEntrada = (parcelas || []).filter(p => p.ehEntrada);

  const dataCurtaBR = (iso) => new Date(`${String(iso).slice(0, 10)}T00:00:00`)
    .toLocaleDateString('pt-BR');

  // Leitura falhada não vira motivo: `entradaEmitida` nulo viraria `false`, e
  // a tela acusaria "entrada a emitir" de um serviço que talvez já tenha uma.
  const motivos = leituraFalhou ? [] : motivosDoServico(service, {
    parcelas,
    valor: valorCalculado,
    entradaPaga,
    entradaEmitida: Boolean(entradaEmitida),
    laboratory: currentLab,
  }).motivos;

  // `motivo` preenchido, e não nulo, porque é ele que faz a seção "Entrada"
  // renderizar. Com nulo ela sumia inteira na falha de leitura.
  const convite = leituraFalhou
    ? { deve: false, motivo: 'leitura_falhou' }
    : entradaAEmitir(service, currentLab, Boolean(entradaEmitida));

  /**
   * A entrada que já saiu errada, dita por extenso.
   *
   * As travas impedem a emissão de hoje em diante e não fazem nada pelo
   * documento que já existe. Sem esta linha, a tela mostrava "Entrada R$ 1.805
   * em fatura 2026-0034" como mostraria qualquer outra — e, enquanto o serviço
   * não é faturado, é a entrada que responde pela conta: o cliente aparecia
   * devendo R$ 1.805 por um trabalho de R$ 1.520.
   */
  const problemas = problemasDaEntrada(service, {
    entradaFaturada: entradaEmitida?.valor || 0,
    valorDoServico: valorCalculado,
  });

  const contato = clients.find(c => c.id === service.client_id)?.contact;
  const faturadoNoServico = services.find(x => x.id === service.billing_service_id);

  const vence = conta.vence && conta.saldo > 0
    ? new Date(`${conta.vence}T00:00:00`).toLocaleDateString('pt-BR')
    : null;
  const venceu = conta.vence && conta.vence < hoje;

  const botao = 'px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50';
  const secundario = `${botao} bg-white text-slate-700 border border-slate-300 hover:bg-slate-50`;

  return (
    <JanelaModal chave="financeiro-do-servico" titulo={nomeDoCliente(service, clients)}
      subtitulo={(contato || service.service_code) && (
        <>
          {contato && <>{contato} · </>}
          {service.service_code && <span className="font-mono">#{service.service_code}</span>}
        </>
      )}
      onFechar={onClose} largura="sm:max-w-lg">
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4">
          {/* ── O que está errado, na língua do Dashboard ──────────────────
              Mesmas frases, mesma conta, mesmos argumentos: quem cobra abre as
              duas telas e precisa reconhecer o serviço na segunda. Vem antes
              da conta porque é o que muda o que se faz agora. */}
          <MotivosDoServico motivos={motivos} dataCurta={dataCurtaBR} className="space-y-1" />

          {/* ── A conta deste serviço ──────────────────────────────────── */}
          {semCobranca ? (
            <p className="text-sm text-slate-600">
              Sem cobrança
              {faturadoNoServico
                ? <> · faturado em <span className="font-mono">#{faturadoNoServico.service_code}</span></>
                : ' · o faturamento acontece em outro serviço'}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {service.billed ? (
                <>
                  <Numero rotulo="Faturado" valor={conta.faturado} />
                  {conta.recebido > 0 && <Numero rotulo="Recebido" valor={conta.recebido} />}
                  {conta.saldo > 0 && (
                    <Numero
                      rotulo="A receber" valor={conta.saldo} forte
                      apoio={vence ? (venceu ? `${MARK.WARN} venceu em ${vence}` : `vence em ${vence}`) : null}
                    />
                  )}
                </>
              ) : (
                <>
                  {valorCalculado > 0 && <Numero rotulo="A faturar" valor={valorCalculado} forte />}
                  {conta.saldo > 0 && (
                    <Numero
                      rotulo="Entrada a receber" valor={conta.saldo}
                      apoio={vence ? (venceu ? `${MARK.WARN} venceu em ${vence}` : `vence em ${vence}`) : null}
                    />
                  )}
                  {valorCalculado <= 0 && conta.saldo <= 0 && (
                    <p className="text-sm text-slate-500 col-span-2">
                      Nada a cobrar ainda: a etapa que fecha o faturamento não foi concluída.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/*
            * O PAPEL FICOU ATRÁS DA CONTA.
            *
            * Antes este aviso dizia que a conta estava errada e mandava refaturar
            * para acertá-la. Já não é isso: a conta segue o cadastro e está certa
            * sozinha. O que ficou velho é o DOCUMENTO que foi ao cliente, e é por
            * isso que refaturar continua valendo — para o papel dizer o mesmo que
            * a cobrança.
            */}
          {service.billed
            && Math.abs(valorCalculado - (parseFloat(service.billed_value) || 0)) > 0.005 && (
            <p className="text-xs text-slate-600">
              {MARK.WARN} A conta usa o preço do cadastro, R$ {reais(valorCalculado)}, e a fatura
              emitida saiu por R$ {reais(parseFloat(service.billed_value) || 0)}. Refature para o
              documento do cliente dizer o mesmo.
            </p>
          )}

          {/* ── A entrada ──────────────────────────────────────────────── */}
          {(entradaEmitida || convite.deve || convite.motivo) && !semCobranca && (
            <div className="p-3 bg-slate-50 rounded border border-slate-200">
              <p className="text-xs font-medium text-slate-700 mb-1">Entrada</p>
              {entradaEmitida ? (
                <>
                  <p className="text-xs text-slate-600">
                    R$ {reais(entradaEmitida.valor)} em fatura {entradaEmitida.numero || ''}
                    {entradaPaga > 0 && ` · R$ ${reais(entradaPaga)} recebida`}
                  </p>
                  {problemas.map(p => (
                    <p key={p} className="text-xs text-slate-800 mt-1">
                      {MARK.WARN} Esta entrada não devia existir: {PROBLEMA_DA_ENTRADA[p]}.
                    </p>
                  ))}
                  {/* A SAÍDA, escrita. A supersessão já existe e é automática:
                      emitir a fatura do serviço cancela a da entrada e move o
                      que foi recebido. Sem dizer isso, a pessoa fica com um
                      aviso e nenhum caminho. */}
                  {problemas.length > 0 && !service.billed && valorCalculado > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Faturar o serviço por R$ {reais(valorCalculado)} cancela esta entrada e
                      transfere para a fatura nova o que já tiver sido recebido.
                    </p>
                  )}
                </>
              ) : convite.deve ? (
                <p className="text-xs text-slate-600">
                  {convite.quantidade} embrião(ões) × R$ {reais(convite.taxa)} ={' '}
                  <span className="font-semibold text-slate-900">R$ {reais(convite.valor)}</span>
                </p>
              ) : (
                <p className="text-xs text-slate-500">{MOTIVO_DA_ENTRADA[convite.motivo]}</p>
              )}
            </div>
          )}

          {forma && (
            <p className="text-xs text-slate-500">Pagamento combinado: {forma}</p>
          )}

          {/* A conversa de cobrança: "vai pagar dia 15". Interna, não sai em
              documento. `key` pelo serviço para não herdar a edição de outro. */}
          {!semCobranca && (
            <ObservacaoDeCobranca
              key={service.id}
              variante="bloco"
              valor={service.observacao_de_cobranca}
              podeMexer={canEdit() && !!salvarObservacaoDeCobranca}
              onSalvar={(texto) => salvarObservacaoDeCobranca(service, texto)}
            />
          )}

          {/* ── As parcelas, uma por linha ─────────────────────────────── */}
          {parcelas.length > 0 && canEdit() && (
            <div>
              <p className="text-xs font-medium text-slate-700 mb-1.5">Parcelas</p>
              <div className="space-y-1">
                {parcelas.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handleMarkAsPaid(service, p)}
                    disabled={loading}
                    className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded border
                               border-slate-200 bg-white hover:bg-slate-50 text-xs disabled:opacity-50"
                  >
                    <span className="text-slate-600">
                      {p.ehEntrada ? 'Entrada' : 'Serviço'} · {p.numero}/{p.total}
                      {p.vencimento && ` · ${new Date(`${String(p.vencimento).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR')}`}
                    </span>
                    <span className={`tabular-nums font-medium ${p.paga_em ? 'text-slate-500' : 'text-slate-900'}`}>
                      R$ {reais(p.valor)}{p.paga_em ? ' · quitada' : ''}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Ações ─────────────────────────────────────────────────────── */}
        <div className="border-t border-slate-200 p-3 bg-slate-50 flex flex-wrap gap-2">
          {!service.billed && canManageServices() && (
            <button onClick={() => openCostsModal(service)} className={secundario}>Custos</button>
          )}

          {convite.deve && canManageServices() && (
            <button
              onClick={() => handleChargeEntrada(service)}
              disabled={loading}
              className={secundario}
              title={`Cobrar entrada de ${calcularEntrada(service, currentLab).quantidade} embriões`}
            >
              Cobrar entrada
            </button>
          )}

          {/* O DOCUMENTO DA ENTRADA, DEPOIS DE EMITIDO.
              Ele era gerado uma única vez, dentro do modal de cobrança, no
              instante da emissão — e nunca mais. Os dois botões de PDF daqui
              e da aba Financeiro exigem `service.billed` e filtram
              `!p.ehEntrada`, com o comentário de que "a entrada tem documento
              próprio": próprio e inalcançável. Quem precisasse reenviar a
              cobrança ao cliente não tinha por onde.

              `emitidaEm` vem da fatura, não de hoje: reimpressão não é
              reemissão, e a data no papel é a do documento que saiu.

              `entradaAbatida: 0` porque aqui a entrada É o documento — não há
              o que abater dela mesma. */}
          {parcelasDaEntrada.length > 0 && canManageServices() && (
            <button
              onClick={() => generateBillingPDF(service, currentLab, clients, {
                modo: 'entrada',
                billingDate: parcelasDaEntrada[0].emitidaEm || hoje,
                notes: '',
                parcelas: parcelasDaEntrada.map(p => ({
                  numero: p.numero, valor: p.valor, vencimento: p.vencimento,
                })),
                entradaAbatida: 0,
              })}
              className={secundario}
              title={`Reimprimir a fatura ${parcelasDaEntrada[0].numeroDaFatura || ''} da entrada`}
            >
              PDF da entrada
            </button>
          )}
          {parcelasDaEntrada.length > 0 && !parcelasDaEntrada.some(p => p.paga_em)
            && canManageServices() && handleRefaturarEntrada && (
            <button onClick={() => handleRefaturarEntrada(service)} disabled={loading} className={secundario}
              title="Apaga a fatura da entrada e a devolve para a emitir, com os transferidos de agora">
              Refaturar entrada
            </button>
          )}

          {!service.billed && valorCalculado > 0 && canManageServices() && (
            <button
              onClick={() => handleBillService(service)}
              disabled={loading}
              className={`${botao} bg-blue-600 text-white hover:bg-blue-700`}
            >
              Faturar
            </button>
          )}

          {service.billed && canEdit() && (
            <label className={`${botao} flex items-center gap-1.5 cursor-pointer border ${
              service.paid
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
              title={service.paid
                ? 'Pago — desmarcar apaga os recebimentos lançados'
                : 'Marcar o serviço inteiro como pago'}
            >
              <input type="checkbox" checked={!!service.paid} disabled={loading}
                onChange={() => handleTogglePago(service)} className="rounded border-slate-300" />
              {service.paid ? 'Pago' : 'Marcar pago'}
            </label>
          )}

          {service.billed && canManageServices() && (
            <>
              <button
                onClick={() => generateBillingPDF(service, currentLab, clients, {
                  billingDate: service.billed_date || hoje,
                  notes: service.billing_notes || '',
                  parcelas: parcelas.filter(p => !p.ehEntrada).map(p => ({
                    numero: p.numero, valor: p.valor, vencimento: p.vencimento,
                  })),
                })}
                className={secundario}
              >
                PDF
              </button>
              <button onClick={() => handleRefaturar(service)} disabled={loading} className={secundario}>
                Refaturar
              </button>
            </>
          )}

          <button onClick={onClose} className={`${secundario} ml-auto`}>Fechar</button>
        </div>
    </JanelaModal>
  );
};

export default FinanceiroDoServicoModal;
